import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Target, Download, Loader2, User, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
    { value: 'modern', label: 'Modern' },
    { value: 'classic', label: 'Classic' },
    { value: 'minimal', label: 'Minimal' }
];

export default function SharedResume() {
    const { shareToken } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resume, setResume] = useState(null);
    const [username, setUsername] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('modern');
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const fetchSharedResume = async () => {
            try {
                const response = await axios.get(`${API}/share/${shareToken}`);
                setResume(response.data.resume);
                setUsername(response.data.user.username);
            } catch (err) {
                setError('Resume not found or is not public');
            } finally {
                setLoading(false);
            }
        };
        fetchSharedResume();
    }, [shareToken]);

    const downloadPDF = async () => {
        setDownloading(true);
        try {
            const response = await axios.get(
                `${API}/share/${shareToken}/pdf?template=${selectedTemplate}`,
                { responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${resume.title.replace(/\s+/g, '_')}_resume.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('PDF download failed:', err);
            // toast not available in shared page, fail silently but log for debugging
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
                <div className="text-center">
                    <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h1 className="text-2xl font-bold mb-2">Resume Not Found</h1>
                    <p className="text-muted-foreground">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#09090b]">
            {/* Header */}
            <header className="border-b border-border">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Target className="w-6 h-6 text-primary" />
                        <span className="font-bold text-lg">LevelUp Resume</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                            <SelectTrigger className="w-32 bg-black/50 border-white/10">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TEMPLATES.map(t => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button 
                            data-testid="download-shared-pdf-btn"
                            onClick={downloadPDF}
                            disabled={downloading}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                            {downloading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    <Download className="w-4 h-4 mr-2" />
                                    Download PDF
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </header>

            {/* Resume Content */}
            <main className="max-w-4xl mx-auto px-6 py-12">
                {/* Header Info */}
                <div className="glass rounded-xl p-8 mb-8">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="w-8 h-8 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold">{username}</h1>
                            {resume.target_job && (
                                <p className="text-primary flex items-center gap-2 mt-1">
                                    <Briefcase className="w-4 h-4" />
                                    {resume.target_job}
                                </p>
                            )}
                        </div>
                    </div>
                    
                    {resume.summary && (
                        <div className="mt-6 p-4 bg-card border border-border rounded-lg">
                            <p className="text-muted-foreground leading-relaxed">{resume.summary}</p>
                        </div>
                    )}
                </div>

                {/* Skills */}
                <div className="glass rounded-xl p-8">
                    <h2 className="text-2xl font-bold mb-6">Core Competencies</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {resume.skills.map((skill, index) => (
                            <div 
                                key={skill.professional_skill ? `skill-${skill.professional_skill}` : `skill-${index}`}
                                className="bg-card border border-border rounded-lg p-4"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-primary/20 rounded-lg shrink-0">
                                        <Target className="w-4 h-4 text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-sm">{skill.professional_skill}</h4>
                                        <p className="text-xs text-muted-foreground mt-1">{skill.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 px-6 border-t border-border">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="text-sm text-muted-foreground">
                        Created with <span className="text-primary font-bold">LevelUp Resume</span> - Transform your gaming skills into career success
                    </p>
                </div>
            </footer>
        </div>
    );
}
